import React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, X, Mountain, Route, TrendingUp, Calendar, MapPin, Map as MapIcon, Info, Camera, Star } from "lucide-react";
import { landmarkCategoryIcon, landmarkCategoryLabel } from "../../lib/landmark-icons";
import { Badge } from "../Badge";
import { Button } from "../Button";
import { ProgressBar } from "../ProgressBar";
import { StarRating } from "../StarRating";
import { useAuth } from "../../hooks/useAuth";
import { fetchTrail } from "../../api/trails";
import { fetchMyLandmarkCheckins, fetchRandomLandmarkCheckin } from "../../api/landmarks";
import type { MapFeatureProperties, FeatureStatus } from "../../api/map";
import type { LandmarkCheckin, RatingSummary } from "@atlas/shared";

const STATUS_META: Record<FeatureStatus, { label: string; variant: "success" | "info" | "neutral" }> = {
  collected: { label: "Collected", variant: "success" },
  in_progress: { label: "In progress", variant: "info" },
  not_collected: { label: "Not visited", variant: "neutral" },
};

/** Credit chip laid over a community photo: "@handle", linked when the
 * explorer's profile is public. */
function PhotoCredit({
  username,
  profilePublic,
  isOwn,
}: {
  username: string | null;
  profilePublic: boolean;
  isOwn: boolean;
}) {
  const label = isOwn ? "Your photo" : username ? `@${username}` : "An explorer";
  const style: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 9px",
    borderRadius: "var(--radius-pill)",
    fontSize: 11.5,
    fontWeight: 600,
    color: "var(--text-primary)",
    background: "var(--surface-overlay)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid var(--border-default)",
    textDecoration: "none",
    whiteSpace: "nowrap",
  };
  const inner = (
    <>
      <Camera size={11} />
      {label}
    </>
  );
  return profilePublic && username && !isOwn ? (
    <Link to="/explorers/$username" params={{ username }} style={style} title={`Photo by @${username}`}>
      {inner}
    </Link>
  ) : (
    <span style={style} title={isOwn ? "Your check-in photo" : `Photo by ${label}`}>{inner}</span>
  );
}

function StatRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "9px 0",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--text-muted)",
          fontSize: 13,
        }}
      >
        {icon}
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          fontWeight: 600,
          color: "var(--text-primary)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function DirectoryRow({
  feature,
  onSelect,
  active,
}: {
  feature: MapFeatureProperties;
  onSelect: (f: MapFeatureProperties) => void;
  active?: boolean;
}) {
  const got = feature.status === "collected";
  const meta =
    feature.type === "peak"
      ? `${feature.elevation ?? "?"} m`
      : feature.type === "trail"
        ? `${feature.distanceKm ?? "?"} km`
        : feature.category ?? "Landmark";

  return (
    <button
      onClick={() => onSelect(feature)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        padding: "8px 9px",
        borderRadius: "var(--radius-md)",
        border: "1px solid " + (active ? "var(--border-gold)" : "transparent"),
        background: active ? "var(--accent-soft)" : "transparent",
        font: "inherit",
        transition: "var(--t-colors)",
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "var(--surface-raised)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          flex: "0 0 auto",
          borderRadius: "var(--radius-md)",
          display: "grid",
          placeItems: "center",
          background: got ? "var(--accent-soft)" : "var(--surface-sunken)",
          border: "1px solid " + (got ? "var(--border-gold)" : "var(--border-default)"),
          color: got ? "var(--gold-400)" : "var(--text-muted)",
        }}
      >
        {feature.type === "trail" ? (
          <Route size={16} />
        ) : feature.type === "peak" ? (
          <Mountain size={16} />
        ) : (
          landmarkCategoryIcon(feature.category, 16)
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--text-primary)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {feature.name}
        </div>
        <div className="eyebrow" style={{ fontSize: 9.5, marginTop: 2 }}>
          {meta}
        </div>
      </div>
      <span
        style={{
          width: 8,
          height: 8,
          flex: "0 0 auto",
          borderRadius: "50%",
          background:
            feature.status === "collected"
              ? "var(--gold-400)"
              : feature.status === "in_progress"
                ? "var(--sky-400)"
                : "var(--text-muted)",
          boxShadow: got ? "var(--glow-gold-sm)" : "none",
        }}
      />
    </button>
  );
}

interface FeatureDetailProps {
  feature: MapFeatureProperties;
  nearby: MapFeatureProperties[];
  onBack: () => void;
  onSelect: (f: MapFeatureProperties) => void;
  /** Live map context: fly the camera to this peak/landmark and highlight it. */
  onFocusMap?: (f: MapFeatureProperties) => void;
  /** Overlay context (collection/region pages): navigate to a scoped map view. */
  onShowOnMap?: (f: MapFeatureProperties) => void;
  /** Mobile bottom-sheet context: compact header (title at top, X close
      top-right) instead of the tall topo hero, so the limited sheet height
      goes to content. */
  sheet?: boolean;
  /**
   * Demo mode (landing page map): render from this fixed data and make no
   * requests. Omitted everywhere inside the app.
   */
  staticData?: FeatureStaticData;
}

export interface FeatureStaticData {
  rating: RatingSummary;
  checkins?: LandmarkCheckin[];
  /** Photo for the header, credited like a community cover. */
  cover?: {
    photoUrl: string;
    caption: string | null;
    isOwn: boolean;
    explorer: { username: string | null; profilePublic: boolean };
  };
}

export function FeatureDetail({ feature, nearby, onBack, onSelect, onFocusMap, onShowOnMap, sheet, staticData }: FeatureDetailProps) {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const st = STATUS_META[feature.status];
  const isTrail = feature.type === "trail";

  const trailQuery = useQuery({
    queryKey: ["trails", feature.id],
    queryFn: () => fetchTrail(feature.id),
    enabled: isTrail && !staticData,
    staleTime: 30_000,
  });
  const trail = trailQuery.data?.trail;

  // Your check-in photos — only fetched when the map feature says you have
  // one (hasCheckin), so plain clicks stay a zero-request inspect. For a peak
  // the check-in lives on its marker-collapsed trig pillar (trigSlug).
  const checkinSlug =
    feature.hasCheckin === true
      ? feature.type === "landmark"
        ? feature.slug
        : feature.type === "peak"
          ? feature.trigSlug ?? null
          : null
      : null;
  const myCheckinsQuery = useQuery({
    queryKey: ["landmarks", checkinSlug, "checkins", "mine"],
    queryFn: () => fetchMyLandmarkCheckins(checkinSlug!),
    enabled: checkinSlug != null && !staticData,
    staleTime: 30_000,
  });
  const myCheckins = staticData ? staticData.checkins ?? [] : myCheckinsQuery.data?.checkins ?? [];

  // Community cover photo — one public, approved check-in picked at random by
  // the API, credited to whoever posted it. Only requested when the map
  // feature says a photo exists (hasPhotos), keeping bare clicks request-free.
  const photoSlug =
    feature.hasPhotos === true
      ? feature.type === "landmark"
        ? feature.slug
        : feature.type === "peak"
          ? feature.trigSlug ?? null
          : null
      : null;
  const coverQuery = useQuery({
    queryKey: ["landmarks", photoSlug, "checkins", "random"],
    queryFn: () => fetchRandomLandmarkCheckin(photoSlug!),
    enabled: photoSlug != null && !staticData,
    staleTime: 0,
  });
  const cover = staticData ? staticData.cover ?? null : coverQuery.data?.checkins[0] ?? null;
  const coverCredit = cover ? (
    <PhotoCredit
      username={cover.explorer.username}
      profilePublic={cover.explorer.profilePublic}
      isOwn={cover.isOwn}
    />
  ) : null;

  const eyebrow =
    feature.type === "peak" ? "Peak" : isTrail ? "Trail" : feature.category ?? "Landmark";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {sheet ? (
        /* Compact sheet header: no hero — title straight at the top, close
           button top-right, every remaining pixel goes to the body. */
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            padding: "16px 18px 12px",
            flex: "0 0 auto",
            borderBottom: "1px solid var(--border-subtle)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow" style={{ fontSize: 10, marginBottom: 5 }}>
              {eyebrow}
            </div>
            <h2
              style={{
                font: "var(--type-h2)",
                fontSize: 22,
                color: "var(--text-primary)",
                margin: 0,
                letterSpacing: "-.01em",
                lineHeight: 1.1,
              }}
            >
              {feature.name}
            </h2>
          </div>
          <button
            onClick={onBack}
            title="Close"
            style={{
              flex: "0 0 auto",
              width: 34,
              height: 34,
              marginTop: 2,
              borderRadius: "var(--radius-pill)",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              color: "var(--text-primary)",
              background: "var(--surface-raised)",
              border: "1px solid var(--border-default)",
            }}
          >
            <X size={17} />
          </button>
        </div>
      ) : (
      /* hero */
      <div style={{ position: "relative", height: cover ? 220 : 168, flex: `0 0 ${cover ? 220 : 168}px`, overflow: "hidden" }}>
        <div
          className="atlas-topo"
          style={{ position: "absolute", inset: 0, background: "var(--surface-sunken)" }}
        />
        {cover && (
          <img
            src={cover.photoUrl}
            alt={cover.caption ?? `Check-in photo at ${feature.name}`}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: cover
              ? "linear-gradient(0deg, var(--surface-card) 0%, color-mix(in srgb, var(--surface-card) 78%, transparent) 34%, color-mix(in srgb, var(--surface-card) 18%, transparent) 62%, transparent)"
              : "linear-gradient(0deg, var(--surface-card) 4%, color-mix(in srgb, var(--surface-card) 30%, transparent) 55%, transparent)",
          }}
        />
        {coverCredit && (
          <div style={{ position: "absolute", top: 14, right: 14 }}>{coverCredit}</div>
        )}
        <button
          onClick={onBack}
          title="Back"
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            width: 34,
            height: 34,
            borderRadius: "var(--radius-pill)",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            color: "var(--text-primary)",
            background: "var(--surface-overlay)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid var(--border-default)",
          }}
        >
          <ArrowLeft size={17} />
        </button>
        <div style={{ position: "absolute", left: 18, right: 18, bottom: 14 }}>
          <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>
            {eyebrow}
          </div>
          <h2
            style={{
              font: "var(--type-h2)",
              fontSize: 24,
              color: "var(--text-primary)",
              margin: 0,
              letterSpacing: "-.01em",
              lineHeight: 1.05,
            }}
          >
            {feature.name}
          </h2>
        </div>
      </div>
      )}

      {/* body */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 18,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {sheet && cover && (
          <div style={{ position: "relative", borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border-subtle)" }}>
            <img
              src={cover.photoUrl}
              alt={cover.caption ?? `Check-in photo at ${feature.name}`}
              loading="lazy"
              style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" }}
            />
            <div style={{ position: "absolute", top: 10, right: 10 }}>{coverCredit}</div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Badge variant={st.variant} dot={feature.status !== "not_collected"}>
            {st.label}
          </Badge>
          {feature.type === "peak" && feature.elevation != null && (
            <Badge variant="neutral">{feature.elevation} m</Badge>
          )}
          {isTrail && feature.distanceKm != null && (
            <Badge variant="neutral">{feature.distanceKm} km</Badge>
          )}
        </div>

        {/* Community rating - one small request per selection; one click to
            rate once the feature is collected. Keyed on feature.id so state
            is shared with the detail page. */}
        <div>
          <div className="eyebrow" style={{ fontSize: 10, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <Star size={12} />
            Rating
          </div>
          <StarRating
            key={feature.id}
            itemType={feature.type}
            itemId={feature.id}
            compact
            isAuthenticated={staticData ? true : !!me}
            initial={staticData?.rating}
            static={staticData != null}
          />
        </div>

        {isTrail && trail && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 7,
              }}
            >
              <span style={{ fontSize: 12.5, color: "var(--text-secondary)", fontWeight: 600 }}>
                Sections walked
              </span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--text-muted)" }}>
                {trail.completedSections} of {trail.totalSections} sections · {trail.totalSections - trail.completedSections} to go
              </span>
            </div>
            <ProgressBar value={trail.completedSections} max={Math.max(trail.totalSections, 1)} color="sky" />
          </div>
        )}

        <div>
          {feature.type === "peak" && (
            <>
              <StatRow label="Elevation" value={`${feature.elevation ?? "—"} m`} icon={<Mountain size={14} color="var(--text-faint)" />} />
              <StatRow label="Status" value={st.label} icon={<MapPin size={14} color="var(--text-faint)" />} />
            </>
          )}
          {isTrail && (
            <>
              <StatRow label="Distance" value={feature.distanceKm != null ? `${feature.distanceKm} km` : "—"} icon={<Route size={14} color="var(--text-faint)" />} />
              <StatRow label="Status" value={st.label} icon={<TrendingUp size={14} color="var(--text-faint)" />} />
            </>
          )}
          {feature.type === "landmark" && (
            <>
              <StatRow
                label="Type"
                value={landmarkCategoryLabel(feature.category)}
                icon={<span style={{ color: "var(--text-faint)", display: "flex" }}>{landmarkCategoryIcon(feature.category, 14)}</span>}
              />
              <StatRow label="Status" value={st.label} icon={<Calendar size={14} color="var(--text-faint)" />} />
            </>
          )}
        </div>

        {myCheckins.length > 0 && (
          <div>
            <div className="eyebrow" style={{ fontSize: 10, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Camera size={12} />
              {feature.type === "peak" ? "Your check-ins at the trig point" : "Your check-ins"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {myCheckins.map((ci) => (
                <figure key={ci.id} style={{ margin: 0 }}>
                  <img
                    src={ci.photoUrl}
                    alt={ci.caption ?? "Your check-in photo"}
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      borderRadius: "var(--radius-md)",
                      display: "block",
                      border: "1px solid var(--border-subtle)",
                    }}
                  />
                  <figcaption style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--text-muted)" }}>
                        {new Date(ci.checkedInAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                      </span>
                      {ci.isPrivate && (
                        <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto" }}>Private</span>
                      )}
                    </div>
                    {ci.caption && (
                      <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>{ci.caption}</div>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {isTrail ? (
            <Button
              variant="primary"
              block
              leftIcon={<Route size={15} />}
              onClick={() => void navigate({ to: "/trails/$id", params: { id: feature.id } })}
            >
              View trail
            </Button>
          ) : (
            <>
              <Button
                variant="primary"
                block
                leftIcon={<Info size={15} />}
                onClick={() => {
                  if (feature.type === "peak") {
                    void navigate({ to: "/peaks/$slug", params: { slug: feature.slug } });
                  } else {
                    void navigate({ to: "/landmarks/$slug", params: { slug: feature.slug } });
                  }
                }}
              >
                View details
              </Button>
              {onShowOnMap ? (
                <Button variant="secondary" block leftIcon={<MapIcon size={15} />} onClick={() => onShowOnMap(feature)}>
                  Show on map
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  block
                  leftIcon={<MapIcon size={15} />}
                  onClick={() => onFocusMap?.(feature)}
                >
                  View on map
                </Button>
              )}
            </>
          )}
        </div>

        {nearby.length > 0 && (
          <>
            <div style={{ height: 1, background: "var(--border-subtle)" }} />
            <div>
              <div className="eyebrow" style={{ fontSize: 10, marginBottom: 10 }}>
                More {feature.type === "peak" ? "peaks" : feature.type === "trail" ? "trails" : "landmarks"} nearby
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {nearby.map((f) => (
                  <DirectoryRow key={f.id} feature={f} onSelect={onSelect} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
